import { SizeLocales } from "@lib/constants/filtersConstants";
import { Button } from "@ui/components/forms/button/Button";
import { Box } from "@ui/components/layout/box/Box";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { shoeConventionForSite } from "@config/site/site-config";
import type { Dispatch, SetStateAction } from "react";
import { SelectDropdownItem } from "@ui/components/overlay/select-dropdown/SelectDropdown";

interface SizeSwitcherProps {
    sizeLocale: SizeLocales;
    setSizeLocale: Dispatch<SetStateAction<SizeLocales>>;
}

export const SizeSwitcher = ({ sizeLocale, setSizeLocale }: SizeSwitcherProps) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            CommonCMS.size.labelEU,
            CommonCMS.size.labelUS,
            CommonCMS.size.labelUK,
            CommonCMS.size.labelCM,
            CommonCMS.size.ariaEU,
            CommonCMS.size.ariaUS,
            CommonCMS.size.ariaUK,
            CommonCMS.size.ariaCM,
        ]);
    })();
    const shoeSizeConvention = shoeConventionForSite;

    const availableShoeSizes = shoeSizeConvention.map((item) => {
        const label = microcopies[CommonCMS.size[`label${item}`]] || item;
        return (
            <SwitcherButton
                key={`avail${item}`}
                label={label}
                setSizeLocale={setSizeLocale}
                sizeLocale={sizeLocale as string}
            />
        );
    });
    return (
        <Box data-testid="size-switcher" sx={{ display: "flex", flexDirection: "row" }}>
            {availableShoeSizes}
        </Box>
    );
};

export const SizeSwitcherDropdown = ({ sizeLocale, setSizeLocale }: SizeSwitcherProps) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            CommonCMS.size.labelEU,
            CommonCMS.size.labelUS,
            CommonCMS.size.labelUK,
            CommonCMS.size.labelCM,
        ]);
    })();
    const shoeSizeConvention = shoeConventionForSite;
    const availableShoeSizes = shoeSizeConvention.map((item) => {
        const label = microcopies[CommonCMS.size[`label${item}`]] || item;
        return (
            <Box key={item} sx={{ display: "flex", flexDirection: "row", maxWidth: "100%" }}>
                <SwitcherButtonItem
                    label={label}
                    setSizeLocale={setSizeLocale}
                    sizeLocale={sizeLocale}
                />
            </Box>
        );
    });
    return (
        <Box
            data-testid="size-selection-buttons"
            sx={{ display: "flex", flexDirection: "row", maxWidth: "100%" }}
        >
            {availableShoeSizes}
        </Box>
    );
};

const SwitcherButton = ({ sizeLocale, setSizeLocale, label }) => {
    const isCurrentLocale =
        sizeLocale.toLowerCase() === label.toLowerCase() || label.includes(sizeLocale);

    // AU is the only market where the label from the CMS (AU/US) and the sizeLocale from size convention (US)...
    // are different, we will add this method here and expand it if needed.
    const isAustraliaSize = label === SizeLocales.AU;

    return (
        <Button
            size="sm"
            sx={{ py: 1, px: 3, h: "fit-content", fontSize: "desktopH4" }}
            variant={isCurrentLocale ? "solid" : "standard"}
            onClick={() => {
                if (isCurrentLocale) return;
                setSizeLocale(isAustraliaSize ? SizeLocales.US : label);
            }}
        >
            <span>{label} </span>
        </Button>
    );
};

const SwitcherButtonItem = ({ sizeLocale, setSizeLocale, label }) => {
    const isCurrentLocale =
        sizeLocale.toLowerCase() === label.toLowerCase() || label.includes(sizeLocale);

    // AU is the only market where the label from the CMS (AU/US) and the sizeLocale from size convention (US)...
    // are different, we will add this method here and expand it if needed.
    const isAustraliaSize = label === SizeLocales.AU;

    return (
        <Button
            size="xs"
            as={SelectDropdownItem}
            fontSize="desktopBodyTextNormal"
            variant={isCurrentLocale ? "solid" : "standard"}
            onClick={() => {
                if (isCurrentLocale) return;
                setSizeLocale(isAustraliaSize ? SizeLocales.US : label);
            }}
            _focusVisible={{
                bg: sizeLocale === label && "black",
                outline: "2px solid black",
                zIndex: 1,
            }}
        >
            <span>{label}</span>
        </Button>
    );
};
